import React, { useState } from 'react';
import axios from 'axios';
import {
  FaBell, FaInfoCircle, FaTrash, FaChartLine, FaStar, FaChevronDown, FaUnlock, FaPaperPlane, FaSync
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, formSelectClass, formInputClass } from './menuTabUtils';

const DEFAULT_STEPS = [
  { step: 1, title: 'Information requirement checklist', status: 'pending' },
  { step: 2, title: 'Pending Queries', status: 'pending' },
  { step: 3, title: 'Draft financials', status: 'pending' },
  { step: 4, title: 'Final Books', status: 'pending' },
];

const BookkeepingMenuTab = ({
  selectedMenuUser,
  setSelectedMenuUser,
  handleBookkeepingServiceUpdate,
  handleRemoveBookkeepingService,
}) => {
  const [bookkeepingFrequency, setBookkeepingFrequency] = useState('');
  const [bookkeepingStartDate, setBookkeepingStartDate] = useState('');
  const [updatingBookkeepingStep, setUpdatingBookkeepingStep] = useState({});
  const [manualReminderLoading, setManualReminderLoading] = useState(false);
  const [toggleReminderLoading, setToggleReminderLoading] = useState(false);

  const handleSendReminder = async () => {
    if (!window.confirm("Send a bookkeeping document upload reminder email to this client now?")) return;
    setManualReminderLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/service/${selectedMenuUser._id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Bookkeeping reminder email sent successfully!');
      if (response.data.bookkeepingService) {
        setSelectedMenuUser({
          ...selectedMenuUser,
          bookkeepingService: response.data.bookkeepingService
        });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder email.');
    } finally {
      setManualReminderLoading(false);
    }
  };

  const handleToggleDailyReminders = async () => {
    setToggleReminderLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/service/${selectedMenuUser._id}/toggle-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Daily reminders updated!');
      if (response.data.bookkeepingService) {
        setSelectedMenuUser({
          ...selectedMenuUser,
          bookkeepingService: response.data.bookkeepingService
        });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to toggle daily reminders.');
    } finally {
      setToggleReminderLoading(false);
    }
  };

  const handleUpdateBookkeepingStepStatus = async (stepNumber, status) => {
    setUpdatingBookkeepingStep((prev) => ({ ...prev, [stepNumber]: true }));
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${selectedMenuUser._id}/step`,
        { stepNumber, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.bookkeepingSteps) {
        setSelectedMenuUser({ ...selectedMenuUser, bookkeepingSteps: response.data.bookkeepingSteps });
      } else {
        const updatedSteps = [...(selectedMenuUser.bookkeepingSteps || DEFAULT_STEPS)];
        const idx = updatedSteps.findIndex((s) => s.step === stepNumber);
        if (idx >= 0) updatedSteps[idx] = { ...updatedSteps[idx], status };
        else updatedSteps.push({ step: stepNumber, title: DEFAULT_STEPS[stepNumber - 1]?.title, status });
        setSelectedMenuUser({ ...selectedMenuUser, bookkeepingSteps: updatedSteps });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update bookkeeping step status.');
    } finally {
      setUpdatingBookkeepingStep((prev) => ({ ...prev, [stepNumber]: false }));
    }
  };

  const stepsList = selectedMenuUser?.bookkeepingSteps?.length > 0
    ? selectedMenuUser.bookkeepingSteps
    : DEFAULT_STEPS;

  const finalSteps = DEFAULT_STEPS.map((ds) => {
    const existing = stepsList.find((s) => s.step === ds.step);
    return existing ? existing : ds;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h6 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FaBell className="text-amber-500" /> Due Date Setup
        </h6>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Frequency</label>
          <select
            value={bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency || ''}
            onChange={(e) => setBookkeepingFrequency(e.target.value)}
            className={formSelectClass}
          >
            <option value="">-- Select Frequency --</option>
            <option value="Monthly">Monthly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Yearly">Yearly</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
          <input
            type="date"
            value={bookkeepingStartDate || formatDateForInput(selectedMenuUser?.bookkeepingService?.startDate)}
            onChange={(e) => setBookkeepingStartDate(e.target.value)}
            className={formInputClass}
          />
        </div>

        <div className="p-3 bg-gray-100 dark:bg-neutral-800 rounded-xl text-sm">
          <strong className="text-gray-900 dark:text-white">Due Date Rules:</strong>
          <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 list-disc list-inside">
            <li><strong>Monthly:</strong> Due Date is 1 month after the start date</li>
            <li><strong>Quarterly:</strong> Due Date is 4 months after the start date</li>
            <li><strong>Yearly:</strong> Due Date is 1 year after the start date</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={() => {
            const freq = bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency;
            const start = bookkeepingStartDate || formatDateForInput(selectedMenuUser?.bookkeepingService?.startDate);
            if (freq && start) {
              handleBookkeepingServiceUpdate(selectedMenuUser._id, freq, start);
              setBookkeepingFrequency('');
              setBookkeepingStartDate('');
            }
          }}
          disabled={!(bookkeepingFrequency || selectedMenuUser?.bookkeepingService?.frequency) || !(bookkeepingStartDate || selectedMenuUser?.bookkeepingService?.startDate)}
          className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
        >
          Save & Calculate Due Date
        </button>

        {selectedMenuUser?.bookkeepingService?.dueDate && (
          <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl border border-blue-100 dark:border-blue-800 mt-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h6 className="font-bold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                <FaInfoCircle className="text-blue-500" /> Current Bookkeeping Setup
              </h6>
              <button
                type="button"
                onClick={() => handleRemoveBookkeepingService(selectedMenuUser._id)}
                className="p-2 bg-red-100 dark:bg-red-900/20 text-red-600 rounded-lg hover:bg-red-200"
                title="Remove Service"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs bg-white dark:bg-neutral-900 p-4 rounded-xl border border-gray-100 dark:border-neutral-700">
              <div>
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Frequency</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{selectedMenuUser.bookkeepingService.frequency}</div>
              </div>
              <div>
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Start Date</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDateForDisplay(selectedMenuUser.bookkeepingService.startDate)}</div>
              </div>
              <div className="col-span-2 pt-2 border-t border-gray-100 dark:border-neutral-700">
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Accounting Period</div>
                <div className="text-xs font-bold text-blue-600 dark:text-blue-400">{selectedMenuUser.bookkeepingService.periodText || "—"}</div>
              </div>
              <div className="col-span-2 pt-2 border-t border-gray-100 dark:border-neutral-700">
                <div className="text-gray-400 uppercase font-semibold text-[10px] mb-0.5">Next Due Date</div>
                <div className="text-sm font-bold text-red-600 dark:text-red-400">
                  {selectedMenuUser.bookkeepingService.dueDateText || formatDateForDisplay(selectedMenuUser.bookkeepingService.dueDate)}
                </div>
              </div>
            </div>

            {/* Document Status */}
            {(() => {
              const isDocUploaded = selectedMenuUser?.bookkeepingService?.documentsUploaded || 
                selectedMenuUser?.bookkeepingSteps?.find(s => s.step === 1)?.status === 'submitted' || 
                selectedMenuUser?.bookkeepingSteps?.find(s => s.step === 1)?.status === 'completed';
              return (
                <div className="p-3 rounded-xl bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-700 shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${isDocUploaded ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-amber-500 animate-pulse'}`} />
                    <div>
                      <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Document Upload Status</div>
                      <div className={`text-xs font-bold ${isDocUploaded ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {isDocUploaded ? 'Documents Uploaded (Daily Reminders Stopped)' : 'Documents Pending (Daily Reminders Active)'}
                      </div>
                    </div>
                  </div>
                  {selectedMenuUser?.bookkeepingService?.documentsUploadedAt && (
                    <span className="text-[10px] text-gray-400 font-medium">
                      {new Date(selectedMenuUser.bookkeepingService.documentsUploadedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Email Reminder Tracking & Manual Actions */}
            <div className="p-4 bg-white dark:bg-neutral-900 rounded-xl border border-gray-100 dark:border-neutral-700 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <FaBell className="text-amber-500" /> Reminder Email Status
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                  selectedMenuUser?.bookkeepingService?.dailyReminderActive !== false && !selectedMenuUser?.bookkeepingService?.documentsUploaded
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-gray-400'
                }`}>
                  {selectedMenuUser?.bookkeepingService?.dailyReminderActive !== false && !selectedMenuUser?.bookkeepingService?.documentsUploaded
                    ? 'Daily Reminders Active'
                    : 'Daily Reminders Stopped'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2.5 bg-gray-50 dark:bg-neutral-800 rounded-lg border border-gray-100 dark:border-neutral-700">
                  <span className="text-[10px] text-gray-400 block font-semibold">Last Reminder Sent</span>
                  <span className="font-bold text-gray-800 dark:text-gray-200 text-xs block truncate mt-0.5">
                    {selectedMenuUser?.bookkeepingService?.lastReminderSentAt 
                      ? new Date(selectedMenuUser.bookkeepingService.lastReminderSentAt).toLocaleString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : 'Never'}
                  </span>
                  {selectedMenuUser?.bookkeepingService?.lastReminderType && (
                    <span className="inline-block mt-1 text-[9px] font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded">
                      {selectedMenuUser.bookkeepingService.lastReminderType === 'manual' ? 'Manual (Staff)' : (selectedMenuUser.bookkeepingService.lastReminderType === 'auto-setup' ? 'Due Date Setup' : 'Daily Cron')}
                    </span>
                  )}
                </div>

                <div className="p-2.5 bg-gray-50 dark:bg-neutral-800 rounded-lg border border-gray-100 dark:border-neutral-700">
                  <span className="text-[10px] text-gray-400 block font-semibold">Total Reminders</span>
                  <span className="font-bold text-gray-800 dark:text-gray-200 text-xs block mt-0.5">
                    {selectedMenuUser?.bookkeepingService?.reminderCount || 0} Sent
                  </span>
                  <span className="block mt-1 text-[9px] text-gray-500 truncate">
                    {selectedMenuUser?.bookkeepingService?.dailyReminderActive !== false && !selectedMenuUser?.bookkeepingService?.documentsUploaded
                      ? 'Scheduled Daily (9 AM UTC)'
                      : 'Reminders Inactive'}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSendReminder}
                  disabled={manualReminderLoading}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50 shadow-sm"
                  title="Send an immediate document upload reminder email to this client"
                >
                  {manualReminderLoading ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaPaperPlane className="w-3 h-3" />
                  )}
                  <span>Send Reminder Email Now</span>
                </button>

                <button
                  type="button"
                  onClick={handleToggleDailyReminders}
                  disabled={toggleReminderLoading}
                  className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all disabled:opacity-50 flex items-center gap-1.5 ${
                    selectedMenuUser?.bookkeepingService?.dailyReminderActive !== false
                      ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30'
                      : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900/30'
                  }`}
                  title="Toggle daily automated reminder emails"
                >
                  {toggleReminderLoading ? (
                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSync className="w-3 h-3" />
                  )}
                  <span>{selectedMenuUser?.bookkeepingService?.dailyReminderActive !== false ? 'Pause Daily' : 'Resume Daily'}</span>
                </button>
              </div>

              {/* Recent Reminder Logs */}
              {selectedMenuUser?.bookkeepingService?.reminderLogs && selectedMenuUser.bookkeepingService.reminderLogs.length > 0 && (
                <div className="pt-2 border-t border-gray-100 dark:border-neutral-700">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">Recent Reminder History</span>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {selectedMenuUser.bookkeepingService.reminderLogs.slice(-3).reverse().map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between text-[10px] p-1.5 rounded bg-gray-50 dark:bg-neutral-800 text-gray-600 dark:text-gray-300">
                        <span className="font-medium">
                          {new Date(log.sentAt).toLocaleString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${
                          log.reminderType === 'manual' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : (log.reminderType === 'auto-setup' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300')
                        }`}>
                          {log.reminderType === 'manual' ? 'Manual' : (log.reminderType === 'auto-setup' ? 'Setup' : 'Daily Cron')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="pt-6 border-t border-gray-100 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaChartLine className="text-primary-500" /> Bookkeeping Steps Status
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {finalSteps.map((step) => (
            <div
              key={step.step}
              className={`p-4 rounded-xl border-2 ${
                step.status === 'completed'
                  ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                  : step.status === 'submitted'
                    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                    : 'bg-gray-50/50 dark:bg-neutral-900/50 border-gray-200 dark:border-neutral-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Step {step.step}</span>
                <span className={`px-2 py-0.5 text-[9px] font-black rounded-full uppercase ${
                  step.status === 'completed' ? 'bg-green-500 text-white'
                    : step.status === 'submitted' ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white'
                }`}>
                  {step.status || 'pending'}
                </span>
              </div>
              <h6 className="font-bold text-gray-900 dark:text-white text-xs mb-3">{step.title}</h6>
              <div className="relative">
                <select
                  value={step.status || 'pending'}
                  onChange={(e) => handleUpdateBookkeepingStepStatus(step.step, e.target.value)}
                  disabled={updatingBookkeepingStep[step.step]}
                  className="w-full pl-3 pr-8 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-xs font-semibold appearance-none"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="client-pending">Client Action Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="completed">Completed</option>
                </select>
                <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-500 pointer-events-none" />
              </div>
              {step.step === 1 && (step.status === 'submitted' || step.status === 'completed') && (
                <button
                  type="button"
                  onClick={() => handleUpdateBookkeepingStepStatus(1, 'in-progress')}
                  className="w-full mt-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"
                >
                  <FaUnlock className="w-3 h-3" /> Re-open for User
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t border-gray-100 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaStar className="text-yellow-400" /> Bookkeeping Feedback
        </h5>
        {selectedMenuUser?.bookkeepingFeedback?.rating ? (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar key={star} size={12} className={star <= selectedMenuUser.bookkeepingFeedback.rating ? 'text-yellow-400' : 'text-gray-200'} />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {selectedMenuUser.bookkeepingFeedback.rating}/5
              </span>
            </div>
            {selectedMenuUser.bookkeepingFeedback.comment && (
              <p className="text-xs text-gray-700 dark:text-gray-300 italic bg-white/50 dark:bg-neutral-900/50 p-3 rounded-lg">
                &ldquo;{selectedMenuUser.bookkeepingFeedback.comment}&rdquo;
              </p>
            )}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 dark:bg-neutral-900/30 border border-gray-100 dark:border-neutral-700 rounded-xl text-center">
            <p className="text-xs text-gray-400">No feedback provided yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookkeepingMenuTab;
